import {
  DataTableCollapsibleRow,
  FormMonthRangePicker,
  FormSelect,
  Heading,
  Loader,
  NoData,
} from "@/components";
import { clearingStatusOptions, formatAmount, yearOptions } from "@/lib/utils";
import {
  Badge,
  Button,
  Checkbox,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import { Check, CheckCircle2, Download, ListFilter, Search, X } from "lucide-react";
import useVendorTrialBalance from "./useVendorTrialBalance";
import { cn } from "@/lib/utils";
import {
  CLEARING_STATUS,
  DATE_RANGE,
  GL_MASTER_IDS,
  SELECTION_CRITERIA,
  selectionCriteriaOptions,
  VENDOR,
  YEAR,
} from "./consts";
import { FormProvider } from "react-hook-form";
import { DocumentLineItemDialog } from "@/pages";

const VendorTrialBalance = () => {
  const {
    breadcrumbData,
    form,
    isTrialBalanceLoading,
    reportColumns,
    setOpenColumnDrawer,
    onSubmit,
    totalDebits,
    totalCredits,
    isBalanced,
    searchValue,
    setSearchValue,
    filteredReportData,
    visibleLineItemColumns,
    isTrialBalanceExporting,
    onDownloadExcel,
    onExpandStateChange,
    isLineItemLoading,
    lineItemData,
    openColumnDrawer,
    onApplyLineItemColumns,
    onChangeLineItemColumn,
    drawerLineItemColumns,
    formData,
    isLineItemReportLoading,
    onDownloadGlExcel,
    onOpenAccountingDocument,
    openDocumentDialog,
    selectedDocument,
    setOpenDocumentDialog,
    isRunClicked,
    displayDate,
    communityData,
    currentFinanceYear,
    onClear,
    isVendorLoading,
    vendorList,
    glOptions,
    isLoadingGlMaster
  } = useVendorTrialBalance();
  const {errors} = form.formState;

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between mb-6">
        <div className="flex flex-row items-center justify-between">
          <Heading title={<span>Vendor Trial Balance <span className="text-[12px]">(FY-{currentFinanceYear})</span></span>} breadcrumbData={breadcrumbData} />
        </div>
      </div>

      {isVendorLoading || isLoadingGlMaster ? <Loader /> :
        <>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px] mb-[30px] shadow-md">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[25px]">
                  <FormSelect 
                    name={SELECTION_CRITERIA}
                    label="Selection Criteria"
                    control={form.control}
                    isRequired
                    error={(errors?.[SELECTION_CRITERIA] as any)?.message}
                    options={selectionCriteriaOptions}
                  />

                  {formData[SELECTION_CRITERIA]?.toLowerCase() == "monthly" ?
                    <FormMonthRangePicker
                      name={DATE_RANGE}
                      label="Month Range"
                      placeholder="Select Range"
                      control={form.control}
                      onClear={() =>
                        form.setValue(DATE_RANGE, { from: null, to: null })
                      }
                      isRequired
                      error={
                        ((errors?.[DATE_RANGE] as any)?.message ||
                          (errors?.[DATE_RANGE] as any)?.from
                            ?.message ||
                          (errors?.[DATE_RANGE] as any)?.to
                            ?.message) as string
                      }
                    /> :
                    <FormSelect
                      name={YEAR}
                      control={form.control}
                      isRequired
                      error={
                        (errors?.[YEAR] as any)?.message}
                      showSearch={false}
                      placeholder="Please Select"
                      label="Select Year"
                      searchPlaceholder=""
                      noDataPlaceholder=""
                      options={yearOptions(10, 3)?.map((d: any) => ({
                        value: d.value,
                        label: d.label,
                      }))}
                    />
                  }

                  <FormSelect 
                    multiSelect
                    name={VENDOR}
                    placeholder="Please Select"
                    label="Vendor Name"
                    control={form.control}
                    options={vendorList?.map((data: any) => ({
                      label: `${data?.vendorName}`,
                      value: data?.vendorId?.toString()
                    }))}
                  />

                  <FormSelect 
                    showSearch={false}
                    name={CLEARING_STATUS}
                    placeholder="Please Select"
                    label="Clearing Status"
                    control={form.control}
                    options={clearingStatusOptions}
                  />

                  <FormSelect
                    multiSelect
                    name={GL_MASTER_IDS}
                    label="GL Account"
                    placeholder="Please Select"
                    control={form.control}
                    options={glOptions}
                  />

                </div>

                {/* <div className="mt-5 pt-5 border-t">
                  <Label>Selected options</Label>
                  <div className="flex gap-3 mt-5 flex-wrap">
                    {activeBadge?.map((data: any) => 
                      <Badge variant="outline" className="text-[12px] rounded-full font-normal">
                        {data?.label || ""}
                        {data?.hasDefault ? <></> : <Button
                          type="button"
                          variant="ghost"
                          className="h-[20px] w-[20px]"
                          onClick={() => onClearField(data?.formKey ? data?.formKey : data?.key, data?.value)}>
                          <X />
                        </Button>}
                      </Badge>
                    )}
                  </div>
                </div> */}
                <div className="mt-6 flex justify-end flex-row gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isTrialBalanceLoading}
                    onClick={onClear}
                  >
                    {isTrialBalanceLoading ? <Loader variant="secondary" /> : <></>} Clear All
                  </Button>
                  <Button
                    type="submit"
                    disabled={isTrialBalanceLoading}
                    className="bg-primary text-white"
                  >
                    {isTrialBalanceLoading ? <Loader variant="secondary" /> : <></>} Run Report
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>

          {isRunClicked ? isTrialBalanceLoading ? <Loader /> : filteredReportData?.length ? (
            <div className="bg-white rounded-lg shadow-md mt-6 overflow-hidden">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between p-5 pb-0">
                <div className="flex flex-col gap-1">
                  <h3 className="text-[18px] font-[600] text-primary">
                    Vendor Trial Balance - {" "}
                    {communityData?.name ? (
                      <span className="font-[600] text-amber-500">
                        {communityData?.name}
                      </span>
                    ) : <></>}
                  </h3>
                  <span className="text-[12px] text-g-400">
                    From {displayDate}
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                  <div className="w-full sm:w-[220px]">
                    <Input
                      placeholder="Search GL/ Description"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      startIcon={<Search className="h-4 w-4" />}
                    />
                  </div>
                  <Button type="button" className="bg-green-700 hover:bg-green-700 text-white" disabled={isTrialBalanceExporting} onClick={onDownloadExcel}>
                    Download Excel
                    {isTrialBalanceExporting ? <Loader variant="secondary" /> : <Download className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <DataTableCollapsibleRow
                data={filteredReportData}
                columns={reportColumns}
                hideFooter
                separateExpandColumn
                containerClassName="border-0 shadow-none rounded-none"
                tableWrapperClassName="px-0"
                expandedCellClassName="p-0"
                onExpandStateChange={onExpandStateChange}
                allowMultipleExpansion={false}
                renderExpandedContent={(_: any) => {
                  const rowData = lineItemData?.data || [];

                  return isLineItemLoading ? <Loader /> : (
                    <div className="px-2 py-1">
                      <div className="rounded-sm bg-[#DCE2EC] p-4">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div>
                            <h4 className="text-[16px] font-[600] text-primary">
                              Line Items for GL {rowData?.glAccountNumber}
                            </h4>
                          </div>
                          <div className="flex gap-3">
                            <Button
                              className="bg-primary text-white h-[32px] px-4 text-[12px]"
                              onClick={() => setOpenColumnDrawer(true)}
                            >
                              <ListFilter className="h-3.5 w-3.5" />
                              Column
                            </Button>
                            <Button className="bg-green-700 hover:bg-green-700 text-white h-[32px] px-4 text-[12px]" disabled={isLineItemReportLoading} onClick={onDownloadGlExcel}>
                              Download GL Excel
                              {isLineItemReportLoading ? <Loader variant="secondary" /> : <Download className="h-3.5 w-3.5" />}
                            </Button>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-3 mt-4 mb-4">
                          <Badge
                            variant="secondary"
                            className="rounded-full bg-white text-g-400 shadow-none font-[400]"
                          >
                            Opening : {`${formatAmount(rowData?.openingBalance || 0)} ${rowData?.openingDirection || ""}`.trim()}
                          </Badge>
                          <Badge
                            variant="secondary"
                            className="rounded-full bg-white text-g-400 shadow-none font-[400]"
                          >
                            Debits : {formatAmount(rowData?.totalDebits || 0)}
                          </Badge>
                          <Badge
                            variant="secondary"
                            className="rounded-full bg-white text-g-400 shadow-none font-[400]"
                          >
                            Credits : {formatAmount(rowData?.totalCredits || 0)}
                          </Badge>
                          <Badge
                            variant="secondary"
                            className="rounded-full bg-white text-g-400 shadow-none font-[400]"
                          >
                            Closing : {rowData?.closingBalance ? `${formatAmount(rowData?.closingBalance)} ${rowData?.closingDirection}` : "0"}
                          </Badge>
                        </div>

                        <Table className="bg-gray-100">
                          <TableHeader>
                            <TableRow>
                              {visibleLineItemColumns.map((column) => (
                                <TableHead
                                  key={column.key}
                                  className={cn("text-black font-[600] bg-slate-100", ["debitAmount", "creditAmount"].includes(column.key) ? "text-right" : "text-left")}
                                >
                                  {column.label}
                                </TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rowData?.lineItems?.length ? (
                              rowData.lineItems.map(
                                (item: any, childIndex: number) => (
                                  <TableRow
                                    key={`line-item-${rowData?.glAccountNumber}-${childIndex}`}
                                    className="bg-blue-50 hover:bg-blue-50"
                                  >
                                    {visibleLineItemColumns.map((column) => (
                                      <TableCell key={column.key} className={cn(["debitAmount", "creditAmount"].includes(column.key) ? "text-right" : "text-left")}>
                                        {column.key === "documentNumber" ? (
                                          <Button
                                            type="button"
                                            variant="link"
                                            className="p-0"
                                            onClick={() =>
                                              onOpenAccountingDocument(
                                                item || ""
                                              )
                                            }
                                          >
                                            {item?.documentNumber || "-"}
                                          </Button>
                                        ) : column.key?.toLowerCase()?.includes("amount") ? (
                                          formatAmount(item?.[column?.key] || "0")
                                        ) : column.key === "clearingIndicator" ? (
                                          item?.["clearingIndicator"]?.toLowerCase() == "yes" ? <Check className="stroke-green-600 h-5 w-5 stroke-3" /> : <X className="stroke-red-600 h-5 w-5 stroke-3" />
                                        ) : (
                                          item?.[column.key] || "-"
                                        )}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                )
                              )
                            ) : (
                              <TableRow className="bg-blue-50">
                                <TableCell
                                  colSpan={visibleLineItemColumns?.length || 1}
                                  className="text-center py-6"
                                >
                                  No Data Found.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>

                        <div className="mt-4 text-[12px] text-primary">
                          Click on Line for further details.
                        </div>
                      </div>
                    </div>
                  );
                }}
              />

              <div className="bg-primary text-white flex items-center justify-between px-8 py-4 text-[12px]">
                <div></div>
                <div className="flex items-center gap-6">
                  <span>Total Debits: {formatAmount(totalDebits)}</span>
                  <span>Total Credits: {formatAmount(totalCredits)}</span>
                  <Badge className={cn("rounded-[3px] border-0 px-3 py-1 gap-2 text-[12px] font-[500]", isBalanced ? "bg-green-600 text-green-200" : "bg-red-600 text-red-200")}>
                    <CheckCircle2 className="h-4 w-4" />
                    {isBalanced ? "Balanced" : `${Number(totalDebits) - Number(totalCredits)}`}
                  </Badge>
                </div>
              </div>
            </div>
          ) : <NoData /> : <></>}

          <Drawer
            open={openColumnDrawer}
            onOpenChange={setOpenColumnDrawer}
            direction="right"
          >
            <DrawerContent className="px-6">
              <DrawerHeader className="px-0">
                <div className="flex items-center justify-between">
                  <DrawerTitle className="text-[20px]">
                    Select Columns for Line Items
                  </DrawerTitle>
                  <X
                    onClick={() => setOpenColumnDrawer(false)}
                    className="cursor-pointer stroke-g-300 hover:stroke-g-400"
                  />
                </div>
              </DrawerHeader>

              <div className="bg-white rounded-lg py-[25px] max-h-[70vh] lg:max-h-[95vh] overflow-y-auto pb-[110px] px-1">
                <div className="grid grid-cols-1 gap-[18px]">
                  {drawerLineItemColumns?.map((column) => (
                    <div
                      key={column.key}
                      className="flex items-center gap-3 text-[12px]"
                    >
                      <Checkbox
                        checked={column.checked}
                        onCheckedChange={(checked) =>
                          onChangeLineItemColumn(column.key, !!checked)
                        }
                      />
                      <span>{column.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="absolute bottom-3 left-0 w-full bg-white px-6 pb-[25px] z-[3]">
                <Button
                  type="button"
                  onClick={onApplyLineItemColumns}
                  className="w-full bg-primary text-white"
                >
                  Apply
                </Button>
              </div>
            </DrawerContent>
          </Drawer>

          {openDocumentDialog && selectedDocument ? (
            <DocumentLineItemDialog open={openDocumentDialog} setOpen={setOpenDocumentDialog} selectedDoc={selectedDocument} />
          ) : <></>}
        </>
      }
    </div>
  );
};

export default VendorTrialBalance;
